/* eslint-disable max-len */
/* eslint-disable consistent-return */
/* eslint-disable no-unused-expressions */
import { useEffect } from 'react';

/**
 * useSafeAreaClass
 *
 * 這個 hook 會在網頁載入與視窗尺寸/方向改變時，動態偵測 `env(safe-area-inset-bottom)`。
 * 若數值大於 0，代表目前容器（Safari、PWA、WKWebView/LIFF、直/橫向）確實存在 safe-area，
 * 便在 <body> 加上 `has-safe-area` class，並把數值寫入 CSS 變數 `--safe-area-bottom`。
 *
 * 為什麼不用裝置偵測（例如 isIOS）？
 * - 裝置偵測只能判斷設備型號，無法得知「當下容器/視口」是否真的有 safe-area。
 * - 同一台 iPhone 在不同容器與方向下，safe-area 可能是 0 或 > 0。
 * - 有些 Android 裝置也可能因挖孔/瀏海或系統設定產生 inset，無法由型號推論。
 *
 * 實作重點：
 * - 以「建立隱形測試元素 + 讀取計算後高度」實測 env 值，比 UA 判斷可靠。
 * - 初始先把 `--safe-area-bottom` 設為 `0px`，避免不支援 env 的環境導致 var() 取值為空。
 * - 監聽 `resize`、`orientationchange` 與 `visualViewport.resize`，並以 rAF 節流避免頻繁重算。
 */

const readSafeAreaBottom = () => {
	const probe = document.createElement('div');
	probe.style.cssText = 'position:absolute;bottom:0;height:env(safe-area-inset-bottom);pointer-events:none;visibility:hidden;';
	document.body.appendChild(probe);
	const h = parseFloat(getComputedStyle(probe).height || '0');
	document.body.removeChild(probe);
	return Number.isFinite(h) ? h : 0;
};

const useSafeAreaClass = (): void => {
	useEffect(() => {
		if (typeof window === 'undefined' || !document?.body) return;

		// 先設一個安全的初始值
		document.documentElement.style.setProperty('--safe-area-bottom', '0px');

		const apply = () => {
			const bottom = readSafeAreaBottom();
			document.documentElement.style.setProperty('--safe-area-bottom', `${bottom}px`);
			document.body.classList.toggle('has-safe-area', bottom > 0);
		};

		apply();

		const vv = (window as any).visualViewport;
		let raf = 0;
		const onResize = () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(apply);
		};

		window.addEventListener('orientationchange', onResize);
		window.addEventListener('resize', onResize);
		vv && vv.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('orientationchange', onResize);
			window.removeEventListener('resize', onResize);
			vv && vv.removeEventListener('resize', onResize);
			cancelAnimationFrame(raf);
		};
	}, []);
};

export default useSafeAreaClass;
